import type { StatusEntity, StatusValue } from './status-label';

// DC08 — chip tone per canonical value. Five tones only; each surface maps a tone
// to its own theme tokens (pending amber = warn, payment_verification blue = info,
// confirmed green = ok, cancelled red = danger — CLAUDE.md design tokens).
// Booking tones follow the customer design's explicit STATUS.tone (bd-core.js):
// the merchant design greys completed/no_show (ST_KEY → paused); the customer
// map is the single source here. Payment-KYC tones follow the merchant
// payChipStyle. Enumerations that are not states (reasons, kinds, tiers,
// channels, events) are `neutral` unless the design colours them.
export type StatusTone = 'ok' | 'warn' | 'info' | 'danger' | 'neutral';

type ToneTable = { readonly [E in StatusEntity]: { readonly [V in StatusValue<E>]: StatusTone } };

export const STATUS_TONES = {
  booking_status: {
    pending: 'warn',
    payment_verification: 'info',
    confirmed: 'ok',
    in_progress: 'info',
    completed: 'ok',
    cancelled: 'danger',
    no_show: 'danger',
    rejected: 'danger',
    expired: 'neutral',
    refunded: 'info',
    running: 'info',
  },
  proof_status: { submitted: 'info', verified: 'ok', rejected: 'danger', superseded: 'neutral' },
  proof_reject_reason: {
    not_received: 'danger',
    trx_mismatch: 'danger',
    amount_short: 'danger',
    other: 'danger',
  },
  offer_status: {
    draft: 'neutral',
    pending_review: 'warn',
    active: 'ok',
    paused: 'neutral',
    expired: 'neutral',
    rejected: 'danger',
    taken_down: 'danger',
    scheduled: 'info',
  },
  review_status: { visible: 'ok', hidden: 'warn', removed: 'danger', flagged: 'warn' },
  dispute_kind: {
    paid_but_rejected: 'neutral',
    complaint: 'neutral',
    refund_claim: 'neutral',
    no_service: 'neutral',
    no_show: 'neutral',
    other: 'neutral',
  },
  dispute_status: { open: 'warn', escalated: 'danger', resolved: 'ok' },
  // Admin RES kinds: refund ok, uphold accent (→ neutral), penalty/warn warn, block danger.
  dispute_resolution: {
    override_confirmed: 'ok',
    refund_recorded: 'ok',
    uphold_merchant: 'neutral',
    warn_customer: 'warn',
    penalty_merchant: 'warn',
    block_customer: 'danger',
    dismissed: 'neutral',
  },
  business_status: {
    draft: 'neutral',
    pending_approval: 'warn',
    active: 'ok',
    suspended: 'danger',
    banned: 'danger',
    rejected: 'danger',
    departed: 'neutral',
  },
  claim_state: {
    unclaimed: 'neutral',
    claim_under_review: 'warn',
    claimed: 'ok',
    claimed_verified: 'ok',
  },
  claim_status: { pending: 'warn', approved: 'ok', rejected: 'danger', withdrawn: 'neutral' },
  application_status: {
    pending_approval: 'warn',
    info_requested: 'info',
    approved: 'ok',
    rejected: 'danger',
  },
  payment_kyc_state: {
    not_started: 'neutral',
    under_review: 'warn',
    approved: 'ok',
    more_info: 'warn',
    rejected: 'danger',
    blocked_individual: 'danger',
  },
  catalog_item_status: {
    draft: 'neutral',
    pending_review: 'warn',
    changes_requested: 'warn',
    active: 'ok',
    rejected: 'danger',
    paused: 'neutral',
    suspended: 'danger',
    archived: 'neutral',
  },
  catalog_visibility: { marketplace: 'neutral', private: 'neutral' },
  plan_tier: {
    free: 'neutral',
    premium: 'neutral',
    business_plus: 'neutral',
    corporate: 'neutral',
  },
  price_kind: { regular: 'neutral', launch: 'info', loyalty: 'info' },
  subscription_status: {
    trial: 'info',
    active: 'ok',
    grace_period: 'warn',
    expired: 'danger',
    cancelled: 'neutral',
  },
  subscription_payment_status: { pending: 'info', verified: 'ok', rejected: 'danger' },
  invoice_status: { paid: 'ok', due: 'warn', overdue: 'danger', void: 'neutral', refunded: 'info' },
  notification_event: {
    booking_confirmed: 'neutral',
    payment_update: 'neutral',
    reminder: 'neutral',
    offers: 'neutral',
    booking_new: 'neutral',
    proof_submitted: 'neutral',
    booking_cancelled: 'neutral',
    review_new: 'neutral',
  },
  notification_channel: {
    sms: 'neutral',
    email: 'neutral',
    push: 'neutral',
    inapp: 'neutral',
    whatsapp: 'neutral',
  },
} as const satisfies ToneTable;

export function statusTone<E extends StatusEntity>(entity: E, value: StatusValue<E>): StatusTone {
  return (STATUS_TONES[entity] as Record<StatusValue<E>, StatusTone>)[value];
}

// Unknown wire values (AP-D10) render neutral.
export function statusToneOrNeutral(entity: StatusEntity, value: string): StatusTone {
  return (STATUS_TONES[entity] as Record<string, StatusTone | undefined>)[value] ?? 'neutral';
}
